import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Loader2, Plus, Radio, Users, Camera, QrCode,
  CheckCircle, XCircle, Eye, Upload, Wifi, WifiOff,
  Play, Square, Image, Copy, ExternalLink, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace('http', 'ws') || 'ws://localhost:8000';

export default function LiveEventPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [liveFeed, setLiveFeed] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const ws = useRef(null);

  // New event form
  const [newEvent, setNewEvent] = useState({
    name: '', gallery_id: '', allow_guest_uploads: true, require_moderation: true
  });

  useEffect(() => {
    fetchEvents();
    fetchGalleries();
    return () => { if (ws.current) ws.current.close(); };
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data.events || []);
    } catch (e) {
      toast.error(language === 'pl' ? 'Blad ladowania eventow' : 'Error loading events');
    } finally {
      setLoading(false);
    }
  };

  const fetchGalleries = async () => {
    try {
      const res = await axios.get(`${API}/galleries`);
      setGalleries(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const createEvent = async () => {
    if (!newEvent.name || !newEvent.gallery_id) {
      toast.error(language === 'pl' ? 'Wypelnij nazwe i wybierz galerie' : 'Fill name and select gallery');
      return;
    }
    try {
      const res = await axios.post(`${API}/events`, newEvent);
      toast.success(language === 'pl' ? 'Event utworzony!' : 'Event created!');
      setShowCreate(false);
      setNewEvent({ name: '', gallery_id: '', allow_guest_uploads: true, require_moderation: true });
      fetchEvents();
      selectEvent(res.data.event_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const selectEvent = async (eventId) => {
    try {
      const res = await axios.get(`${API}/events/${eventId}`);
      setSelectedEvent(res.data);
      connectWebSocket(eventId);
    } catch (e) {
      toast.error('Error loading event');
    }
  };

  const connectWebSocket = useCallback((eventId) => {
    if (ws.current) ws.current.close();

    const wsUrl = `${WS_BASE}/api/ws/event/${eventId}?user_id=${user?.id || 'photographer'}`;
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      setWsConnected(true);
      toast.success(language === 'pl' ? 'Polaczono z live feed' : 'Connected to live feed');
    };

    socket.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'user_joined' || msg.type === 'user_left') {
          setViewerCount(msg.users_count || 0);
        } else if (msg.type === 'new_photo') {
          setLiveFeed(prev => [msg, ...prev].slice(0, 50));
        } else if (msg.type === 'moderation') {
          setLiveFeed(prev => prev.map(item =>
            item.photo_id === msg.photo_id ? { ...item, moderation: msg.action } : item
          ));
        } else if (msg.type === 'reaction') {
          setLiveFeed(prev => prev.map(item =>
            item.photo_id === msg.photo_id
              ? { ...item, reactions: [...(item.reactions || []), msg.emoji] }
              : item
          ));
        }
      } catch (err) { /* ignore parse errors */ }
    };

    socket.onclose = () => setWsConnected(false);
    socket.onerror = () => setWsConnected(false);
    ws.current = socket;
  }, [user, language]);

  const moderatePhoto = async (photoId, action) => {
    if (!selectedEvent) return;
    try {
      await axios.post(`${API}/events/${selectedEvent.id}/moderate/${photoId}?action=${action}`);
      toast.success(action === 'approve'
        ? (language === 'pl' ? 'Zdjecie zatwierdzone' : 'Photo approved')
        : (language === 'pl' ? 'Zdjecie odrzucone' : 'Photo rejected')
      );
    } catch (e) {
      toast.error('Error moderating photo');
    }
  };

  const copyGuestLink = () => {
    if (!selectedEvent) return;
    const link = `${window.location.origin}/event/${selectedEvent.id}/join`;
    navigator.clipboard.writeText(link);
    toast.success(language === 'pl' ? 'Link skopiowany!' : 'Link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/live-events" />

      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-1 flex items-center gap-3">
              <Radio className="w-8 h-8 text-red-500" />
              {language === 'pl' ? 'Live Events' : 'Live Events'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'pl'
                ? 'Transmisja zdjec na zywo — goscie skanuja QR i ogladaja galerie w czasie rzeczywistym'
                : 'Live photo streaming — guests scan QR and view gallery in real-time'}
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {language === 'pl' ? 'Nowy Event' : 'New Event'}
          </Button>
        </div>

        {/* Create Event Form */}
        <AnimatePresence>
          {showCreate && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8"
            >
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'pl' ? 'Nowy Live Event' : 'New Live Event'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'pl' ? 'Nazwa eventu' : 'Event Name'}</Label>
                      <Input
                        value={newEvent.name}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={language === 'pl' ? 'np. Wesele Ani i Marka' : 'e.g. Wedding Reception'}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'pl' ? 'Galeria' : 'Gallery'}</Label>
                      <select
                        value={newEvent.gallery_id}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, gallery_id: e.target.value }))}
                        className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      >
                        <option value="">{language === 'pl' ? '-- Wybierz galerie --' : '-- Select gallery --'}</option>
                        {galleries.map(g => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={newEvent.allow_guest_uploads}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, allow_guest_uploads: e.target.checked }))}
                        className="rounded"
                      />
                      {language === 'pl' ? 'Zezwol na upload gosci' : 'Allow guest uploads'}
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={newEvent.require_moderation}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, require_moderation: e.target.checked }))}
                        className="rounded"
                      />
                      {language === 'pl' ? 'Wymagaj moderacji' : 'Require moderation'}
                    </label>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={createEvent}>
                      <Play className="w-4 h-4 mr-2" />
                      {language === 'pl' ? 'Utwórz event' : 'Create Event'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowCreate(false)}>
                      {language === 'pl' ? 'Anuluj' : 'Cancel'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Event List */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">{language === 'pl' ? 'Twoje eventy' : 'Your Events'}</h2>
            {events.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Radio className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {language === 'pl' ? 'Brak eventow — utwórz pierwszy!' : 'No events — create your first one!'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              events.map(event => (
                <Card
                  key={event.id}
                  className={`cursor-pointer transition-all hover:border-primary/30 ${
                    selectedEvent?.id === event.id ? 'border-primary' : ''
                  }`}
                  onClick={() => selectEvent(event.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{event.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(event.created_at).toLocaleDateString('pl-PL')}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        event.status === 'active'
                          ? 'bg-green-500/10 text-green-500'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {event.status === 'active' ? (language === 'pl' ? 'Aktywny' : 'Active') : event.status}
                      </span>
                    </div>
                    <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                      {event.allow_guest_uploads && (
                        <span className="flex items-center gap-1">
                          <Upload className="w-3 h-3" /> Upload
                        </span>
                      )}
                      {event.require_moderation && (
                        <span className="flex items-center gap-1">
                          <Shield className="w-3 h-3" /> Moderacja
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Event Detail / Live Feed */}
          <div className="lg:col-span-2">
            {selectedEvent ? (
              <div className="space-y-6">
                {/* Event Info Bar */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div>
                          <h2 className="text-xl font-bold">{selectedEvent.name}</h2>
                          <p className="text-sm text-muted-foreground">
                            {language === 'pl' ? 'Kod dostepu' : 'Access code'}: <span className="font-mono font-bold">{selectedEvent.access_code}</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {wsConnected ? (
                            <span className="flex items-center gap-1 text-green-500 text-sm">
                              <Wifi className="w-4 h-4" />
                              Live
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-muted-foreground text-sm">
                              <WifiOff className="w-4 h-4" />
                              Offline
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Eye className="w-4 h-4" />
                          {viewerCount} {language === 'pl' ? 'widzów' : 'viewers'}
                        </div>
                        <Button variant="outline" size="sm" onClick={copyGuestLink}>
                          <Copy className="w-4 h-4 mr-1" />
                          {language === 'pl' ? 'Link dla gosci' : 'Guest Link'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => {
                          // Generate QR placeholder
                          toast.info(language === 'pl'
                            ? `QR do wydruku: ${window.location.origin}/event/${selectedEvent.id}/join`
                            : `QR for print: ${window.location.origin}/event/${selectedEvent.id}/join`
                          );
                        }}>
                          <QrCode className="w-4 h-4 mr-1" />
                          QR
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Live Photo Feed */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Camera className="w-5 h-5" />
                    {language === 'pl' ? 'Live Feed' : 'Live Feed'}
                    {wsConnected && (
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    )}
                  </h3>

                  {liveFeed.length === 0 ? (
                    <Card>
                      <CardContent className="p-12 text-center">
                        <Image className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-muted-foreground">
                          {language === 'pl'
                            ? 'Czekam na zdjecia... Upload zdjecia do galerii lub pozwol gosciom uploadowac.'
                            : 'Waiting for photos... Upload to the gallery or let guests upload.'}
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <AnimatePresence>
                        {liveFeed.map((item, idx) => (
                          <motion.div
                            key={item.photo_id || idx}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="relative"
                          >
                            <Card className="overflow-hidden">
                              <div className="aspect-square bg-secondary flex items-center justify-center">
                                {item.thumbnail_url ? (
                                  <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <Camera className="w-8 h-8 text-muted-foreground" />
                                )}
                              </div>
                              {selectedEvent.require_moderation && !item.moderation && (
                                <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/60 flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="flex-1 text-green-400 hover:text-green-300 hover:bg-green-500/20"
                                    onClick={() => moderatePhoto(item.photo_id, 'approve')}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="flex-1 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                    onClick={() => moderatePhoto(item.photo_id, 'reject')}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                              {item.moderation && (
                                <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                                  item.moderation === 'approve'
                                    ? 'bg-green-500/80 text-white'
                                    : 'bg-red-500/80 text-white'
                                }`}>
                                  {item.moderation === 'approve' ? 'OK' : 'X'}
                                </div>
                              )}
                              {item.reactions?.length > 0 && (
                                <div className="absolute top-2 left-2 bg-black/60 rounded px-1.5 py-0.5 text-xs">
                                  {item.reactions.slice(0, 3).join('')} {item.reactions.length > 3 ? `+${item.reactions.length - 3}` : ''}
                                </div>
                              )}
                            </Card>
                            <p className="text-xs text-muted-foreground mt-1 truncate">
                              {item.guest_name || (language === 'pl' ? 'Fotograf' : 'Photographer')}
                              {item.timestamp && ` · ${new Date(item.timestamp).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`}
                            </p>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Radio className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">
                    {language === 'pl' ? 'Wybierz event' : 'Select an event'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'pl'
                      ? 'Wybierz event z listy lub utwórz nowy aby rozpoczac transmisje na zywo.'
                      : 'Select an event from the list or create a new one to start live streaming.'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
